import {
  MessageSquare,
  FolderKanban,
  Briefcase,
  Megaphone,
  AlertTriangle,
  Clock,
  TrendingUp,
  ArrowRight,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { useNavigate } from "react-router-dom";

interface StatsData {
  contacts: { total: number; new: number; read: number; replied: number; completed: number; lastWeek: number };
  projects: { total: number; active: number; byProgramType: Record<string, number>; upcomingDeadlines: number; overdueReminders: number };
  callPeriods: { active: number; upcomingDeadlines: number };
  clients: { total: number };
  admins: number;
}

const PROGRAM_LABELS: Record<string, string> = {
  yatirim_tesvik: "Yatırım Teşvik",
  kosgeb: "KOSGEB",
  tkdk_ipard: "TKDK / IPARD",
  ihracat: "İhracat",
  tubitak: "TÜBİTAK",
  kalkinma_ajansi: "Kalkınma Ajansı",
};

export function DashboardPage() {
  const { data, loading, error, refetch } = useFetch<{ data: StatsData }>("/stats");
  const navigate = useNavigate();

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data?.data) return null;

  const s = data.data;
  const alerts = s.projects.upcomingDeadlines + s.projects.overdueReminders + s.callPeriods.upcomingDeadlines;

  return (
    <div className="space-y-6">
      {/* Uyarılar */}
      {alerts > 0 && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
          <AlertTriangle className="size-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium text-foreground">Dikkat Gerektiren Konular</p>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {s.projects.upcomingDeadlines > 0 && (
                <span className="flex items-center gap-1"><Clock className="size-3 text-amber-400" />{s.projects.upcomingDeadlines} proje deadline yaklaşıyor</span>
              )}
              {s.projects.overdueReminders > 0 && (
                <span className="flex items-center gap-1"><AlertTriangle className="size-3 text-red-400" />{s.projects.overdueReminders} geciken hatırlatıcı</span>
              )}
              {s.callPeriods.upcomingDeadlines > 0 && (
                <span className="flex items-center gap-1"><Megaphone className="size-3 text-amber-400" />{s.callPeriods.upcomingDeadlines} çağrı deadline yaklaşıyor</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ana Metrikler — tek satır */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <MiniStat label="İletişim Formu" value={s.contacts.total} sub={s.contacts.new > 0 ? `${s.contacts.new} yeni` : undefined} accent={s.contacts.new > 0} onClick={() => navigate("/forms")} />
        <MiniStat label="Proje" value={s.projects.total} sub={s.projects.active > 0 ? `${s.projects.active} aktif` : undefined} accent={s.projects.active > 0} onClick={() => navigate("/projects")} />
        <MiniStat label="Müşteri" value={s.clients.total} onClick={() => navigate("/clients")} />
        <MiniStat label="Çağrı" value={s.callPeriods.active} sub="aktif" accent={s.callPeriods.active > 0} onClick={() => navigate("/call-periods")} />
        <MiniStat label="Admin" value={s.admins} onClick={() => navigate("/users")} />
        <MiniStat label="Son 7 Gün" value={s.contacts.lastWeek} sub="başvuru" />
      </div>

      {/* İki sütun: Formlar + Projeler */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* İletişim Formları Özeti */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="size-4 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">İletişim Formları</h3>
            </div>
            <button onClick={() => navigate("/forms")} className="flex items-center gap-1 text-xs text-accent hover:underline">
              Tümü <ArrowRight className="size-3" />
            </button>
          </div>
          <div className="space-y-2">
            <BarItem label="Okunmamış" value={s.contacts.new} total={s.contacts.total} color="bg-accent" />
            <BarItem label="Okundu" value={s.contacts.read} total={s.contacts.total} color="bg-blue-500" />
            <BarItem label="Yanıtlandı" value={s.contacts.replied} total={s.contacts.total} color="bg-green-500" />
            <BarItem label="Tamamlandı" value={s.contacts.completed} total={s.contacts.total} color="bg-muted-foreground" />
          </div>
        </div>

        {/* Projeler Özeti */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FolderKanban className="size-4 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">Projeler</h3>
            </div>
            <button onClick={() => navigate("/projects")} className="flex items-center gap-1 text-xs text-accent hover:underline">
              Tümü <ArrowRight className="size-3" />
            </button>
          </div>
          {s.projects.total === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Henüz proje bulunmuyor.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-3xl font-bold text-foreground">{s.projects.active}</span>
                <span className="text-xs text-muted-foreground">/ {s.projects.total} toplam</span>
              </div>
              <p className="text-xs text-muted-foreground">aktif proje</p>
              {Object.keys(s.projects.byProgramType).length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-border/50">
                  {Object.entries(s.projects.byProgramType).map(([key, count]) => (
                    <span key={key} className="rounded-md border border-border/50 bg-secondary/50 px-2 py-0.5 text-xs text-muted-foreground">
                      {PROGRAM_LABELS[key] || key}: <span className="font-semibold text-foreground">{count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hızlı Aksiyonlar */}
      <div>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Hızlı Aksiyonlar
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickLink icon={Briefcase} label="Yeni Müşteri Ekle" onClick={() => navigate("/clients")} />
          <QuickLink icon={FolderKanban} label="Yeni Proje Oluştur" onClick={() => navigate("/projects")} />
          <QuickLink icon={Megaphone} label="Çağrı Dönemi Ekle" onClick={() => navigate("/call-periods")} />
          <QuickLink icon={Users} label="Kullanıcı Yönetimi" onClick={() => navigate("/users")} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, accent, onClick }: { label: string; value: number; sub?: string; accent?: boolean; onClick?: () => void }) {
  return (
    <div onClick={onClick} className={cn("rounded-xl border border-border bg-card px-4 py-3 transition-all", onClick && "cursor-pointer hover:bg-secondary/50")}>
      <p className={cn("text-2xl font-bold", accent ? "text-accent" : "text-foreground")}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className="text-[10px] text-accent mt-0.5">{sub}</p>}
    </div>
  );
}

function BarItem({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 text-xs text-muted-foreground">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}

function QuickLink({ icon: Icon, label, onClick }: { icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground transition-all hover:bg-secondary/50 hover:text-foreground">
      <Icon className="size-4" />
      {label}
    </button>
  );
}
